import {
	type Currency,
	type Dtos,
	formatToDollar,
	SuretyBondTermAdjustmentType,
} from "@inrev/common";
import { Option } from "@inrev/core";
import type { FC, ReactNode } from "react";
import { formatFromISODate } from "../../../utils";
import { Card, Icon, Text } from "../../primitives";
import {
	formatSignedDollars,
	PaymentStatusTag,
	SignedDollars,
	TermStatusTag,
} from "./bondTermDisplay";

type BondTerm = Dtos.SuretyBondTerm.Get.Response;
type BondTermAdjustment = Dtos.SuretyBondTerm.Adjustment.Get.Response;

export type CurrentBondTermCardProps = {
	term: BondTerm;
	ordinal: number;
	commission: Currency;
};

const SummaryValue = ({
	label,
	testId,
	children,
}: {
	label: string;
	testId?: string;
	children: ReactNode;
}) => (
	<div className="flex min-w-0 flex-col gap-2.5 px-4 py-4" data-testid={testId}>
		<Text color="tertiary" type="eyebrow">
			{label}
		</Text>
		<Text color="primary" type="title">
			{children}
		</Text>
	</div>
);

const AdjustmentType = ({ adjustment }: { adjustment: BondTermAdjustment }) => {
	if (adjustment.type === SuretyBondTermAdjustmentType.Base) {
		return <Text type="body-strong">Base</Text>;
	}

	return (
		<div className="flex items-center gap-1">
			<Icon
				aria-hidden
				size="micro"
				type={adjustment.amount < 0 ? "arrow-small-down" : "arrow-small-up"}
			/>
			<Text type="body-strong">{adjustment.amount < 0 ? "Decrease" : "Increase"}</Text>
		</div>
	);
};

const AdjustmentRow = ({ adjustment }: { adjustment: BondTermAdjustment }) => (
	<div className="grid grid-cols-[6rem_7rem_minmax(0,1fr)_auto] items-center gap-4 border-b border-bc-subtle px-4 py-2.5 last:border-b-0">
		<PaymentStatusTag status={adjustment.paymentStatus} />
		<AdjustmentType adjustment={adjustment} />
		<Text color="secondary" truncate type="body">
			{SuretyBondTermAdjustmentType.displayName(adjustment.type)}
		</Text>
		<div className="flex justify-self-end">
			{adjustment.type === SuretyBondTermAdjustmentType.Base ? (
				<Text className="tabular-nums" color="primary" type="body-strong">
					{formatToDollar(adjustment.amount)}
				</Text>
			) : (
				<SignedDollars amount={adjustment.amount} type="body" />
			)}
		</div>
	</div>
);

export const CurrentBondTermCard: FC<CurrentBondTermCardProps> = ({
	term,
	ordinal,
	commission,
}) => {
	const [{ amount: basePremium }] = term.adjustments;

	return (
		<Card data-testid="current-bond-term-card" elevation="raised">
			<Card.Header className="border-b-0 px-5 py-5">
				<div className="flex min-w-0 flex-col gap-3.5">
					<Text color="primary" type="title">
						Current Term
					</Text>
					<Text color="tertiary" type="body">
						Term {ordinal} · {formatFromISODate(term.effectiveDate)} –{" "}
						{Option.getOrElse(formatFromISODate(term.expirationDate), "Ongoing")}
					</Text>
				</div>
				<TermStatusTag term={term} />
			</Card.Header>
			<Card.Body className="flex flex-col gap-4 px-5 pb-5 pt-0">
				<div className="grid grid-cols-4 divide-x divide-bc-subtle rounded-md border border-bc-default">
					<SummaryValue label="Base Premium">{formatToDollar(basePremium)}</SummaryValue>
					<SummaryValue label="Adjustments">
						{formatSignedDollars(term.adjustmentsTotal)}
					</SummaryValue>
					<SummaryValue label="Total Premium">{formatToDollar(term.totalPremium)}</SummaryValue>
					<SummaryValue label="Commission" testId="current-term-commission">
						{formatToDollar(commission)}
					</SummaryValue>
				</div>
				<div className="overflow-hidden rounded-md border border-bc-default">
					{term.adjustments.map((adjustment) => (
						<AdjustmentRow adjustment={adjustment} key={adjustment.id} />
					))}
				</div>
			</Card.Body>
		</Card>
	);
};
